<template>
  <section class="zs-card" :class="{ 'zs-editing': editing, 'zs-bare': bare }">
    <header v-if="!bare" class="zs-head">
      <span class="zs-icon"><i :class="icon"></i></span>
      <h3 class="zs-title" :title="description">{{ title }}</h3>
      <span class="zs-spacer"></span>
      <slot name="head"></slot>
    </header>
    <div class="zs-body">
      <slot></slot>
    </div>

    <!-- customize mode: the whole card is the drag handle; the charts underneath do not get the pointer -->
    <div v-if="editing" class="zs-edit-layer drag-handle" :title="'Drag to move · ' + title">
      <span class="zs-grip"><i class="fas fa-arrows-alt"></i> {{ title }}</span>
      <button type="button" class="zs-hide" title="Hide this card" @mousedown.stop @touchstart.stop @click.stop="$emit('hide')">
        <i class="fas fa-eye-slash"></i>
      </button>
    </div>
  </section>
</template>

<script>
export default {
  name: 'StatsCard',
  props: {
    title: { type: String, required: true },
    icon: { type: String, default: 'fas fa-chart-bar' },
    description: { type: String, default: '' },
    editing: { type: Boolean, default: false },
    bare: { type: Boolean, default: false }
  }
};
</script>

<style scoped lang="scss">
.zs-card {
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--card-bg);
  border: 1px solid var(--border-subtle, rgba(0, 0, 0, 0.06));
  border-radius: 20px;
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease;

  &:hover {
    box-shadow: var(--shadow-lg);
    border-color: rgba(var(--accent-green-rgb), 0.25);
  }
}

.zs-bare {
  background: transparent;
  border: none;
  box-shadow: none;
  &:hover { box-shadow: none; }
}

.zs-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px 4px;
  min-height: 48px;
}

.zs-icon {
  width: 30px;
  height: 30px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  color: var(--accent-green);
  background: rgba(var(--accent-green-rgb), 0.12);
  flex: 0 0 auto;
}

.zs-title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--text-heading);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.zs-spacer { flex: 1 1 auto; }

.zs-body {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.zs-edit-layer {
  position: absolute;
  inset: 0;
  z-index: 5;
  cursor: grab;
  border-radius: inherit;
  border: 2px dashed rgba(var(--accent-green-rgb), 0.55);
  background: rgba(var(--accent-green-rgb), 0.06);
  backdrop-filter: blur(1px);
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px;
  animation: zs-wiggle-in 0.25s ease;

  &:active { cursor: grabbing; }
}

.zs-grip {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--accent-green);
  background: var(--card-bg);
  border-radius: 999px;
  padding: 4px 10px;
  box-shadow: var(--shadow-sm);
  max-width: calc(100% - 50px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  i { margin-right: 4px; }
}

.zs-hide {
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--card-bg);
  color: var(--text-muted);
  box-shadow: var(--shadow-sm);
  cursor: pointer;
  transition: color 0.2s, transform 0.2s;

  &:hover { color: #d9534f; transform: scale(1.08); }
}

@keyframes zs-wiggle-in {
  from { opacity: 0; transform: scale(0.98); }
  to { opacity: 1; transform: scale(1); }
}
</style>
